import React, { useEffect, useMemo, useState } from "react";
import PortalLayout from "@/components/PortalLayout";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Search, TrendingUp, TrendingDown, Wallet, Clock } from "lucide-react";

const ron = (v) => `${(v || 0).toLocaleString("ro-RO", { maximumFractionDigits: 0 })} RON`;

function BurnBar({ pct }) {
  if (pct == null) return <span className="text-[11px] text-slate-300">fără buget</span>;
  const clamped = Math.min(100, pct);
  const color = pct > 100 ? "#dc2626" : pct > 80 ? "#d97706" : "#16a34a";
  return (
    <div className="min-w-[120px]">
      <div className="flex justify-between mb-1">
        <span className="text-[11px] font-semibold tabular-nums" style={{ color }}>{pct}%</span>
      </div>
      <div className="pio-progress"><div style={{ width: `${clamped}%`, background: color }} /></div>
    </div>
  );
}

export default function Budgets() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [onlyActive, setOnlyActive] = useState(true);

  useEffect(() => {
    api.get("/pm/budgets")
      .then(r => setRows(r.data))
      .catch(e => toast.error(apiErr(e)))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let r = rows;
    if (onlyActive) r = r.filter(x => x.hours > 0);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      r = r.filter(x => x.project_name.toLowerCase().includes(s) || (x.project_code || "").toLowerCase().includes(s));
    }
    return r;
  }, [rows, q, onlyActive]);

  const kpi = useMemo(() => {
    const act = rows.filter(x => x.hours > 0);
    const revenue = act.reduce((s, x) => s + x.revenue, 0);
    const cost = act.reduce((s, x) => s + x.total_cost, 0);
    const margin = revenue - cost;
    const over = act.filter(x => x.hours_pct != null && x.hours_pct > 100).length;
    return { revenue, cost, margin, marginPct: revenue ? (margin / revenue * 100) : 0, over, count: act.length };
  }, [rows]);

  return (
    <PortalLayout variant="staff">
      <div data-testid="budgets-page">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-5 gap-3">
          <div>
            <div className="section-eyebrow">Proiecte</div>
            <h1 className="section-title">Bugete</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 sm:flex-none">
              <Search className="h-3.5 w-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2" />
              <input data-testid="budgets-search" value={q} onChange={e => setQ(e.target.value)}
                placeholder="Caută proiect..." className="pio-input pr-8 w-full sm:w-52" />
            </div>
            <label className="flex items-center gap-1.5 text-[12px] text-slate-600 cursor-pointer select-none whitespace-nowrap">
              <input type="checkbox" checked={onlyActive} onChange={e => setOnlyActive(e.target.checked)}
                data-testid="budgets-only-active" className="accent-[#3d68b1]" />
              Doar cu activitate
            </label>
          </div>
        </div>

        {loading ? (
          <div className="py-24 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-5">
              <KpiCard testId="kpi-revenue" icon={Wallet} label="Venit total" value={ron(kpi.revenue)} accent="#3d68b1" />
              <KpiCard testId="kpi-cost" icon={Clock} label="Cost total" value={ron(kpi.cost)} accent="#8B6E4A" />
              <KpiCard testId="kpi-margin" icon={kpi.margin >= 0 ? TrendingUp : TrendingDown} label="Marjă"
                value={`${ron(kpi.margin)} · ${kpi.marginPct.toFixed(1)}%`}
                accent={kpi.margin >= 0 ? "#16a34a" : "#dc2626"} />
              <KpiCard testId="kpi-overbudget" icon={TrendingDown} label="Peste buget ore" value={`${kpi.over} / ${kpi.count} proiecte`}
                accent={kpi.over > 0 ? "#dc2626" : "#16a34a"} />
            </div>

            <div className="surface overflow-hidden">
              <div className="max-h-[calc(100vh-320px)] overflow-y-auto overflow-x-auto">
                <table className="pio-table">
                  <thead>
                    <tr>
                      <th className="w-[24%]">Proiect</th>
                      <th className="text-right">Ore / Buget</th>
                      <th>Ore utilizate</th>
                      <th className="text-right">Venit</th>
                      <th>Buget utilizat</th>
                      <th className="text-right">Cost total</th>
                      <th className="text-right">Marjă</th>
                      <th className="text-right">Luna curentă</th>
                      <th className="text-right">Echipă</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 && (
                      <tr><td colSpan={9} className="py-12 text-center text-slate-400">Niciun proiect găsit.</td></tr>
                    )}
                    {filtered.map(r => (
                      <tr key={r.project_id} data-testid={`budget-row-${r.project_id}`}>
                        <td>
                          <div className="font-semibold text-[13px] text-slate-800 leading-tight">{r.project_name}</div>
                          <div className="text-[11px] text-slate-400">{r.project_code}</div>
                        </td>
                        <td className="text-right tabular-nums text-[13px]">
                          <span className="font-semibold text-slate-700">{r.hours}</span>
                          <span className="text-slate-400"> / {r.budget_hours || "—"}</span>
                        </td>
                        <td><BurnBar pct={r.hours_pct} /></td>
                        <td className="text-right tabular-nums text-[13px] font-medium text-slate-700">{ron(r.revenue)}</td>
                        <td><BurnBar pct={r.budget_pct} /></td>
                        <td className="text-right tabular-nums text-[13px] text-slate-600">{ron(r.total_cost)}</td>
                        <td className="text-right">
                          <div className={`tabular-nums text-[13px] font-semibold ${r.margin >= 0 ? "text-green-700" : "text-red-600"}`}>{ron(r.margin)}</div>
                          {r.margin_pct != null && (
                            <div className={`text-[11px] tabular-nums ${r.margin >= 0 ? "text-green-600" : "text-red-500"}`}>{r.margin_pct}%</div>
                          )}
                        </td>
                        <td className="text-right tabular-nums text-[12px] text-slate-500">{r.month_hours}h</td>
                        <td className="text-right tabular-nums text-[12px] text-slate-500">{r.team_size}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </PortalLayout>
  );
}

function KpiCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <div className="surface p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <div className="h-7 w-7 rounded-md flex items-center justify-center" style={{ background: `${accent}14`, color: accent }}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
      </div>
      <div className="text-[19px] font-bold tabular-nums text-slate-800 font-display">{value}</div>
    </div>
  );
}
